import { Injectable, ErrorHandler, NgZone } from '@angular/core';
import { Router, NavigationError } from '@angular/router';

/**
 * v1.6.253 — Stale-deploy recovery.
 *
 * Problem:
 * When a new version is deployed while operators have the app open, the page's
 * in-memory `index.html` still references chunk filenames (like
 * `chunk-64JQD3OZ.js`) from the previous build. On lazy-route navigation Angular
 * tries to dynamically `import()` those files; the server has replaced them with
 * new hashes and returns its SPA fallback (the new `index.html`, MIME `text/html`),
 * which the browser rightfully rejects as a non-module. The route load throws
 * `ChunkLoadError` / `Failed to fetch dynamically imported module` and the user
 * sees a broken page with no way forward.
 *
 * Fix:
 * Detect these errors (from the Angular global ErrorHandler AND from the Router's
 * NavigationError stream), then hard-reload the page so the browser fetches the
 * fresh bundle. A single reload is performed per session to avoid infinite loops
 * in the pathological case where the server is actually broken.
 *
 * Note: this is intentionally narrow. We only match the specific failure shapes
 * that indicate stale-deploy; any other error falls through to the default
 * Angular error handler so normal bugs still surface in the console.
 */
@Injectable({ providedIn: 'root' })
export class ChunkErrorHandler implements ErrorHandler {
  private reloaded = false;
  /** sessionStorage key used to cap reloads at one per session */
  private static readonly SENTINEL = '__chunk_reloaded_at';

  constructor(private zone: NgZone, private router: Router) {
    // Catch lazy-route load failures from the Router as well — these come through
    // as NavigationError events and bypass the synchronous ErrorHandler path.
    this.router.events.subscribe(evt => {
      if (evt instanceof NavigationError && this.isChunkLoadError(evt.error)) {
        this.recoverOrSurface(evt.error);
      }
    });
  }

  handleError(error: any): void {
    if (this.isChunkLoadError(error)) {
      this.recoverOrSurface(error);
      return;
    }
    // Not a chunk-load failure — keep default behaviour so real bugs aren't swallowed.
    console.error(error);
  }

  /** True if the error looks like a lazy-chunk load failure caused by a stale deploy. */
  private isChunkLoadError(error: any): boolean {
    if (!error) return false;
    const name = error.name || '';
    const msg  = (error.message || error.toString() || '').toString();
    // Browser / bundler-reported shapes, in order of frequency observed:
    //  - TypeError: Failed to fetch dynamically imported module
    //  - TypeError: error loading dynamically imported module
    //  - ChunkLoadError (Webpack/Vite lazy chunk)
    //  - Loading chunk XX failed
    //  - Loading CSS chunk XX failed
    //  - Expected a JavaScript-or-Wasm module script but the server responded with
    //    a MIME type of "text/html"  (strict MIME check in Safari/Chrome)
    if (name === 'ChunkLoadError') return true;
    return (
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg) ||
      /Loading chunk [^\s]+ failed/i.test(msg) ||
      /Loading CSS chunk [^\s]+ failed/i.test(msg) ||
      /Expected a JavaScript-or-Wasm module/i.test(msg)
    );
  }

  /**
   * Reload once per session. If we've already reloaded and still see chunk
   * errors, we give up and log — something is actually broken on the server
   * and infinite reloads would punish the operator.
   */
  private recoverOrSurface(error: any) {
    if (this.reloaded) {
      console.error('[ChunkErrorHandler] Chunk load still failing after reload. Not reloading again.', error);
      return;
    }
    const priorReload = this.safeReadSentinel();
    // Cap to one reload per 30 seconds — if we somehow navigate back into the
    // same lazy route during the reload, don't loop.
    if (priorReload && Date.now() - priorReload < 30_000) {
      console.error('[ChunkErrorHandler] Recently reloaded; skipping to avoid loop.', error);
      return;
    }
    this.reloaded = true;
    this.safeWriteSentinel();
    console.warn('[ChunkErrorHandler] Stale deploy detected — reloading.', error);
    // Run the reload outside Angular's zone so no change-detection cycle kicks in.
    this.zone.runOutsideAngular(() => {
      // Small delay so the current microtask / error log finishes flushing.
      setTimeout(() => window.location.reload(), 50);
    });
  }

  private safeReadSentinel(): number | null {
    try {
      const v = sessionStorage.getItem(ChunkErrorHandler.SENTINEL);
      return v ? parseInt(v, 10) || null : null;
    } catch { return null; }
  }

  private safeWriteSentinel() {
    try { sessionStorage.setItem(ChunkErrorHandler.SENTINEL, String(Date.now())); } catch {}
  }
}
